import React, { useEffect, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { fetchPublicSettings } from '../services/publicContentService';

/* ---------- Bilingual content ---------- */
const CONTENT = {
  ar: {
    eyebrow: 'سياسة الخصوصية',
    title: 'سياسة الخصوصية',
    company: 'شركة التوازن البيئي',
    intro:
      'مرحباً بكم في موقع شركة التوازن البيئي. نحن نلتزم بحماية وخصوصية بياناتكم الشخصية والتعامل معها بشفافية تامة وفقاً للأنظمة واللوائح المعمول بها. توضح هذه السياسة كيفية جمع بياناتكم، استخدامها، وحمايتها عند زيارتكم لموقعنا الإلكتروني أو الاستفادة من خدماتنا.',
    lastUpdated: 'آخر تحديث: 2026',
    sections: [
      {
        n: '1',
        h: 'البيانات التي نجمعها',
        p: 'نقوم بجمع نوعين من البيانات لتقديم أفضل خدمة ممكنة لكم:',
        items: [
          'بيانات تقدمونها طواعية: وتشمل (الاسم، البريد الإلكتروني، رقم الهاتف، اسم المنشأة، وتفاصيل الاستفسار أو الخدمة المطلوبة) عند تعبئة نماذج التواصل أو طلب الاستشارات.',
          'بيانات يتم جمعها تلقائياً: وتشمل عنوان بروتوكول الإنترنت (IP)، نوع المتصفح، نظام التشغيل، والصفحات التي قمت بزيارتها عبر ملفات تعريف الارتباط (Cookies) لتحسين تجربة المستخدم.',
        ],
      },
      {
        n: '2',
        h: 'كيف نستخدم بياناتكم؟',
        p: 'نستخدم المعلومات التي نجمعها للأغراض التالية:',
        items: [
          'تقديم الخدمات والاستشارات البيئية التي تطلبونها.',
          'الرد على استفساراتكم وتوفير الدعم الفني.',
          'إرسال التحديثات، العروض، أو النشرات البريدية (يمكنكم إلغاء الاشتراك بها في أي وقت).',
          'تحسين أداء الموقع الإلكتروني وتطوير خدماتنا بما يتناسب مع احتياجاتكم.',
        ],
      },
      {
        n: '3',
        h: 'مشاركة البيانات مع أطراف ثالثة',
        p: 'نحن لا نبيع، ولا نؤجر، ولا نتاجر ببياناتكم الشخصية مع أي جهات خارجية. قد يتم مشاركة البيانات فقط في الحالات التالية:',
        items: [
          'مع مزودي الخدمات الموثوقين الذين يساعدوننا في تشغيل الموقع أو إدارة أعمالنا (بشرط التزامهم بسرية البيانات).',
          'الامتثال للأنظمة واللوائح القانونية أو استجابة لطلب من الجهات الحكومية المختصة.',
        ],
      },
      {
        n: '4',
        h: 'حماية وأمن البيانات',
        p: 'نطبق أعلى معايير الأمان التقنية والإدارية لحماية بياناتكم من الفقدان، أو الاستخدام غير المصرح به، أو التعديل، أو الإفشاء. ومع ذلك، يرجى العلم أنه لا توجد وسيلة نقل عبر الإنترنت آمنة بنسبة 100%.',
        items: [],
      },
      {
        n: '5',
        h: 'حقوقكم بشأن بياناتكم الشخصية',
        p: 'بموجب أنظمة حماية البيانات، يحق لكم:',
        items: [
          'الوصول إلى بياناتكم الشخصية التي نحتفظ بها وطلب نسخة منها.',
          'تحديث أو تصحيح أي بيانات غير دقيقة.',
          'طلب مسح بياناتكم الشخصية (ما لم تكن هناك أسباب قانونية تمنع ذلك).',
          'سحب موافقتكم على معالجة البيانات في أي وقت.',
        ],
      },
      {
        n: '6',
        h: 'التعديلات على سياسة الخصوصية',
        p: 'نحتفظ بالحق في تحديث سياسة الخصوصية هذه من وقت لآخر لتوائم المستجدات القانونية أو الفنية. سيتم نشر أي تغييرات على هذه الصفحة مع تحديث التاريخ في أسفل الصفحة.',
        items: [],
      },
    ],
  },
  en: {
    eyebrow: 'Privacy Policy',
    title: 'Privacy Policy',
    company: 'Environmental Balance Company',
    intro:
      'Welcome to the website of Environmental Balance Company. We are committed to protecting the privacy of your personal data and handling it with complete transparency in accordance with applicable rules and regulations. This policy explains how we collect, use, and protect your data when you visit our website or benefit from our services.',
    lastUpdated: 'Last Updated: 2026',
    sections: [
      {
        n: '1',
        h: 'Data We Collect',
        p: 'We collect two types of data to provide you with the best possible service:',
        items: [
          'Data you provide voluntarily: This includes (name, email address, phone number, organization name, and details of the inquiry or requested service) when filling out contact forms or requesting consultations.',
          'Data collected automatically: This includes the Internet Protocol (IP) address, browser type, operating system, and the pages you visited via cookies to improve the user experience.',
        ],
      },
      {
        n: '2',
        h: 'How Do We Use Your Data?',
        p: 'We use the information we collect for the following purposes:',
        items: [
          'Providing the environmental services and consultations you request.',
          'Responding to your inquiries and providing technical support.',
          'Sending updates, offers, or newsletters (you can unsubscribe from them at any time).',
          'Improving website performance and developing our services to suit your needs.',
        ],
      },
      {
        n: '3',
        h: 'Sharing Data with Third Parties',
        p: 'We do not sell, rent, or trade your personal data with any external parties. Data may only be shared in the following cases:',
        items: [
          'With trusted service providers who assist us in operating the website or managing our business (provided they commit to data confidentiality).',
          'Compliance with legal rules and regulations or in response to a request from competent government authorities.',
        ],
      },
      {
        n: '4',
        h: 'Data Protection and Security',
        p: 'We apply the highest technical and administrative security standards to protect your data from loss, unauthorized use, modification, or disclosure. However, please be aware that no method of transmission over the Internet is 100% secure.',
        items: [],
      },
      {
        n: '5',
        h: 'Your Rights Regarding Your Personal Data',
        p: 'Under data protection regulations, you have the right to:',
        items: [
          'Access your personal data that we hold and request a copy of it.',
          'Update or correct any inaccurate data.',
          'Request the erasure of your personal data (unless there are legal reasons preventing this).',
          'Withdraw your consent to data processing at any time.',
        ],
      },
      {
        n: '6',
        h: 'Amendments to the Privacy Policy',
        p: 'We reserve the right to update this privacy policy from time to time to align with legal or technical developments. Any changes will be posted on this page with the updated date at the bottom of the page.',
        items: [],
      },
    ],
  },
};

const PrivacyPolicyPage = () => {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const staticContent = isAr ? CONTENT.ar : CONTENT.en;

  // CMS-driven privacy policy. Every field falls back to the built-in static
  // content when empty. `sections` accepts an array of {n, h_ar, h_en, p_ar,
  // p_en, items_ar[], items_en[]} for full editorial control.
  const [cms, setCms] = useState({});
  useEffect(() => {
    let alive = true;
    fetchPublicSettings('privacy_policy').then((v) => { if (alive) setCms(v || {}); });
    return () => { alive = false; };
  }, []);

  const pickBi = (obj, fallback) => {
    if (!obj || typeof obj !== 'object') return fallback;
    const v = isAr ? (obj.ar || obj.en) : (obj.en || obj.ar);
    return (v && String(v).trim()) || fallback;
  };

  // Build effective sections list. If admin supplied any sections, use them;
  // otherwise show the static ones.
  const cmsSections = Array.isArray(cms.sections) ? cms.sections : [];
  const effectiveSections = cmsSections.length > 0
    ? cmsSections.map((s, i) => {
        const staticS = staticContent.sections[i] || { n: String(i + 1), items: [] };
        const itemsList = isAr
          ? (Array.isArray(s.items_ar) ? s.items_ar : (Array.isArray(s.items_en) ? s.items_en : staticS.items))
          : (Array.isArray(s.items_en) ? s.items_en : (Array.isArray(s.items_ar) ? s.items_ar : staticS.items));
        return {
          n: s.n || staticS.n,
          h: pickBi({ ar: s.h_ar, en: s.h_en }, staticS.h),
          p: pickBi({ ar: s.p_ar, en: s.p_en }, staticS.p || ''),
          items: itemsList.filter(Boolean),
        };
      })
    : staticContent.sections;

  const c = {
    eyebrow:     pickBi(cms.eyebrow,      staticContent.eyebrow),
    title:       pickBi(cms.title,        staticContent.title),
    company:     pickBi(cms.company,      staticContent.company),
    intro:       pickBi(cms.intro,        staticContent.intro),
    lastUpdated: pickBi(cms.last_updated, staticContent.lastUpdated),
    sections:    effectiveSections,
  };

  return (
    <div dir={isAr ? 'rtl' : 'ltr'} className="relative overflow-hidden bg-white dark:bg-[#070d18]">
      {/* ===== Hero ===== */}
      <section className="relative pt-32 pb-20 overflow-hidden bg-gradient-to-b from-brand-blue-50 via-cyan-50/60 to-white dark:from-[#06192c] dark:via-[#081a2d] dark:to-[#070d18]">
        <div className="pointer-events-none absolute top-10 left-1/4 w-[420px] h-[420px] rounded-full blur-[120px] bg-brand-blue/15 dark:bg-brand-blue/20" />
        <div className="pointer-events-none absolute bottom-0 right-1/4 w-[420px] h-[420px] rounded-full blur-[120px] bg-cyan-300/15 dark:bg-cyan-500/15" />

        <div className="relative container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center animate-fade-in-up">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand-blue/10 border border-brand-blue/30 mb-6">
              <ShieldCheck className="w-4 h-4 text-brand-blue-700 dark:text-brand-blue-300" />
              <span className="text-xs tracking-[0.3em] uppercase text-brand-blue-800 dark:text-brand-blue-200 font-semibold">
                {c.eyebrow}
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 text-slate-900 dark:text-white tracking-tight">
              {c.title}
            </h1>
            <p className="text-base md:text-lg text-slate-600 dark:text-slate-300">
              {c.company}
            </p>
          </div>
        </div>
      </section>

      {/* ===== Content ===== */}
      <section className="relative py-20">
        <div className="container mx-auto px-4">
          <article
            className={`max-w-4xl mx-auto bg-white dark:bg-white/[0.03]
                       rounded-3xl border border-slate-200 dark:border-white/10
                       shadow-[0_30px_60px_-25px_rgba(58,122,186,0.18)]
                       backdrop-blur-xl p-8 md:p-12 lg:p-14
                       ${isAr ? 'text-right' : 'text-left'}`}
          >
            {/* Intro */}
            <p
              className={`text-base md:text-lg leading-loose text-slate-700 dark:text-slate-200 mb-12 ${isAr ? 'text-right' : 'text-left'}`}
              style={isAr ? { textAlign: 'justify', textAlignLast: 'right' } : undefined}
            >
              {c.intro}
            </p>

            {/* Sections */}
            <div className="space-y-12">
              {c.sections.map((s, i) => (
                <section key={i} data-testid={`privacy-section-${i + 1}`}>
                  <h2 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white leading-tight mb-4">
                    {s.h}
                  </h2>

                  {s.p && (
                    <p
                      className={`text-base leading-loose text-slate-700 dark:text-slate-300 mb-4 ${isAr ? 'text-right' : 'text-left'}`}
                      style={isAr ? { textAlign: 'justify', textAlignLast: 'right' } : undefined}
                    >
                      {s.p}
                    </p>
                  )}

                  {s.items.length > 0 && (
                    <ul className="space-y-3">
                      {s.items.map((it, idx) => (
                        <li
                          key={idx}
                          className="flex items-start gap-3 text-base leading-loose text-slate-700 dark:text-slate-300"
                        >
                          <span className="flex-shrink-0 mt-2 w-1.5 h-1.5 rounded-full bg-brand-blue dark:bg-brand-blue-300" />
                          <span
                            className={`flex-1 ${isAr ? 'text-right' : 'text-left'}`}
                            style={isAr ? { textAlign: 'justify', textAlignLast: 'right' } : undefined}
                          >
                            {it}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              ))}
            </div>

            {/* Last updated */}
            <div className={`mt-14 pt-8 border-t border-slate-200 dark:border-white/10 ${isAr ? 'text-right' : 'text-left'}`}>
              <p className="text-sm text-slate-500 dark:text-slate-400 font-semibold tracking-wide">
                {c.lastUpdated}
              </p>
            </div>
          </article>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicyPage;
